import { useCallback, useEffect, useState } from 'react';
import { useFeature, useGrowthBook } from '@growthbook/growthbook-react';
import DashboardLayout from '../../layouts/DashoardLayout';
import { Box, CircularProgress, Paper, Stack, Typography } from '../../ui/core';
import { surfaceCardSx as cardSx, cardHeaderSx, cardBodySx } from '../../ui/styles';
import api from '../../lib/api';
import { useHasPermission } from '../../helpers/api/auth/index';
import CreateThrottlePanel from './CreateThrottlePanel';
import ActiveThrottlesTable from './ActiveThrottlesTable';
import HistoryThrottlesTable from './HistoryThrottlesTable';

// The reviewed mockup lays this page out as a single column: a plain page
// header, then three stacked cards (create / active / history). That's a
// column of cards rather than the row-of-cards `flushPageSx` scaffold other
// admin pages use, so the outer scaffolding is composed by hand here instead.
const pageSx = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  p: '12px',
  minHeight: '100vh',
  overflowY: 'auto',
};

const HISTORY_PAGE_LIMIT = 50;

// `cursor: null/undefined` means "first page" to the API (TripThrottlingApi
// omits the param entirely when it's falsy) — the same value a stream's
// `nextCursor` takes on once that stream is exhausted. So the cursor alone
// can't tell "haven't paged yet" apart from "no more pages"; exhaustion is
// tracked in its own flag per stream, and a stream stops being fetched (not
// re-fetched from page one) once its flag flips.
const INITIAL_HISTORY_CURSORS = { CANCELLED: undefined, EXPIRED: undefined };
const INITIAL_HISTORY_EXHAUSTED = { CANCELLED: false, EXPIRED: false };

const TripThrottlingShell = () => {
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [historyCursors, setHistoryCursors] = useState(INITIAL_HISTORY_CURSORS);
  const [historyExhausted, setHistoryExhausted] = useState(INITIAL_HISTORY_EXHAUSTED);

  const refetchActive = useCallback(async () => {
    setLoadingActive(true);
    try {
      const result = await api.tripThrottling.getTripThrottles({ status: 'ACTIVE', limit: 100 });
      setActive((result && result.entries) || []);
    } finally {
      setLoadingActive(false);
    }
  }, []);

  const fetchHistoryPage = useCallback(async (cursors, exhausted) => {
    const [cancelled, expired] = await Promise.all([
      exhausted.CANCELLED
        ? null
        : api.tripThrottling.getTripThrottles({ status: 'CANCELLED', cursor: cursors.CANCELLED, limit: HISTORY_PAGE_LIMIT }),
      exhausted.EXPIRED
        ? null
        : api.tripThrottling.getTripThrottles({ status: 'EXPIRED', cursor: cursors.EXPIRED, limit: HISTORY_PAGE_LIMIT }),
    ]);
    return {
      entries: [...((cancelled && cancelled.entries) || []), ...((expired && expired.entries) || [])],
      cursors: {
        CANCELLED: cancelled ? cancelled.nextCursor || null : cursors.CANCELLED,
        EXPIRED: expired ? expired.nextCursor || null : cursors.EXPIRED,
      },
      exhausted: {
        CANCELLED: exhausted.CANCELLED || !(cancelled && cancelled.nextCursor),
        EXPIRED: exhausted.EXPIRED || !(expired && expired.nextCursor),
      },
    };
  }, []);

  const refetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { entries, cursors, exhausted } = await fetchHistoryPage(INITIAL_HISTORY_CURSORS, INITIAL_HISTORY_EXHAUSTED);
      setHistory(entries);
      setHistoryCursors(cursors);
      setHistoryExhausted(exhausted);
    } finally {
      setLoadingHistory(false);
    }
  }, [fetchHistoryPage]);

  const loadMoreHistory = useCallback(async () => {
    setLoadingMoreHistory(true);
    try {
      const { entries, cursors, exhausted } = await fetchHistoryPage(historyCursors, historyExhausted);
      setHistory((prev) => [...prev, ...entries]);
      setHistoryCursors(cursors);
      setHistoryExhausted(exhausted);
    } finally {
      setLoadingMoreHistory(false);
    }
  }, [fetchHistoryPage, historyCursors, historyExhausted]);

  const hasMoreHistory = !historyExhausted.CANCELLED || !historyExhausted.EXPIRED;

  const refetchAll = useCallback(() => {
    refetchActive();
    refetchHistory();
  }, [refetchActive, refetchHistory]);

  useEffect(() => {
    refetchActive();
    refetchHistory();
  }, [refetchActive, refetchHistory]);

  return (
    <Box sx={pageSx}>
      <Stack spacing="16px" sx={{ maxWidth: 960, width: '100%' }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.01em' }}>
            New trip throttling
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '13px', mt: '4px' }}>
            Pause new-trip creation for a city and day. Seats on existing shared trips stay bookable.
          </Typography>
        </Box>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Create a pause</Typography>
          </Box>
          <Box sx={cardBodySx}>
            <CreateThrottlePanel onCreated={refetchActive} />
          </Box>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Active pauses</Typography>
          </Box>
          <Box sx={cardBodySx}>
            <ActiveThrottlesTable rows={active} loading={loadingActive} onCancelled={refetchAll} />
          </Box>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>History</Typography>
          </Box>
          <Box sx={cardBodySx}>
            <HistoryThrottlesTable
              rows={history}
              loading={loadingHistory}
              hasMore={hasMoreHistory}
              loadingMore={loadingMoreHistory}
              onLoadMore={loadMoreHistory}
            />
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export { TripThrottlingShell };

// The route is registered unconditionally (see App.js) so a flagged-off
// deep-link 404s here instead of the Switch's trailing catch-all silently
// redirecting to /home. `useFeature` reads the same GrowthBook context
// DashoardLayout's nav item does, so the two stay consistent.
//
// App.js kicks off `growthbook.loadFeatures()` app-wide but doesn't block
// rendering on it (a flag-service hiccup shouldn't be able to stall login).
// That means a direct deep-link here can render before this flag's real
// value has arrived, which would 404-flash a page that's actually on. So
// this route alone awaits its own readiness — everywhere else in the app
// stays unblocked.
const TripThrottling = () => {
  const growthbook = useGrowthBook();
  const flagOn = useFeature('admin-trip-throttling').on;
  // Limit to admins holding the `trip_throttle` permission (ADMIN superusers
  // bypass), matching how other sensitive admin features gate access. Note this
  // is a UI gate only — the admin API itself is not permission-enforced yet
  // (tracked separately in HITCH-5249). Treated like flag-off (see below) so a
  // non-permitted deep-link 404s rather than advertising the tool.
  const hasPermission = useHasPermission('trip_throttle');
  const [flagReady, setFlagReady] = useState(!!growthbook?.ready);

  useEffect(() => {
    if (!growthbook || flagReady) return undefined;
    let cancelled = false;
    growthbook.loadFeatures({ timeout: 2000 }).finally(() => {
      if (!cancelled) setFlagReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [growthbook, flagReady]);

  if (!flagReady) {
    return (
      <DashboardLayout title="Trip Throttling">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: '48px' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (!flagOn || !hasPermission) {
    return (
      <DashboardLayout title="Not found">
        <Typography>Page not found.</Typography>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout flush title="New Trip Throttling">
      <TripThrottlingShell />
    </DashboardLayout>
  );
};

export default TripThrottling;
